'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { LuPlus } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import AccountIcons from '@/features/veluvit/components/shell/account-icons'
import BarGlass from '@/features/veluvit/components/shell/bar-glass'
import Logo from '@/features/veluvit/components/shell/logo'
import SafeToggle from '@/features/veluvit/components/shell/safe-toggle'
import {
  activeTab,
  MAIN_NAV,
} from '@/features/veluvit/components/shell/nav-items'
import {
  GemIcon /* , TicketIcon — [2차 개발] 해금권 */,
} from '@/features/veluvit/components/ui/currency-icon'
import GemCount from '@/features/veluvit/components/ui/gem-count'
import {
  PILL,
  PILL_ICON /* , PILL_PAD — [2차 개발] 해금권 */,
} from '@/features/veluvit/components/ui/pill-class'
// import { useAppData } from '@/features/veluvit/hooks/use-app-data' — [2차 개발] 해금권(tickets)만 읽었다
import {
  openCharge /* , openTicketsInfo — [2차 개발] 해금권 */,
} from '@/features/veluvit/services/wallet.actions'

/**
 * PC 상단 막대(1001px~) — 왼쪽 대화 패널(side-nav) 오른쪽 위에 얹힌 유리 막대. 왼쪽에 메뉴(MAIN_NAV), 오른쪽 끝에 재화 · 알림 · 마이. 로고는 왼쪽 패널 맨 위.
 * 왼쪽 패널이 없는 화면은 로고 자리가 사라지므로 `logo` 로 막대 맨 왼쪽에 로고를 둔다(지금은 패널이 없는 스튜디오에 이 막대도 없어 쓰이지 않는다. 구조는 남겨 둠).
 * 붙는 줄(홈 칩 · 화면 탭 줄)이 있으면 유리가 그 밑까지 내려온다(BarGlass). 모바일은 머리줄 + 하단 탭 + 메뉴 서랍(layout-policy.md 규칙 4).
 */
export default function Gnb({ logo = false }: { logo?: boolean }) {
  const k = activeTab(usePathname())
  // const { tickets } = useAppData() — [2차 개발] 해금권

  return (
    <nav
      aria-label="상단 막대"
      data-topnav
      className="absolute top-0 right-0 left-(--side-w) z-50 hidden h-14.5 transition-[left] duration-[240ms] ease-smooth items-center gap-4.5 px-7 over10:flex over10:max-over12:gap-2 over10:max-over12:px-4"
    >
      {/* 유리 바탕 — 홈 칩 줄이 붙으면 그 밑까지 늘어나 한 장이 된다 */}
      <BarGlass on="desk" />
      {logo && (
        <Link
          href="/"
          aria-label="홈"
          className="relative mr-3 flex-none no-underline"
        >
          <Logo className="text-c22" />
        </Link>
      )}
      <nav aria-label="주요 메뉴" className="flex h-full items-center">
        {MAIN_NAV.map((tab) => {
          const on = tab.k === k
          return (
            <Link
              key={tab.k}
              href={tab.href}
              aria-current={on ? 'page' : undefined}
              className={cn(
                // 밑줄 없이 글자 색만(홈 시안 A) — 고른 메뉴는 흰 글자, 나머지는 옅게
                'flex h-full items-center px-3 text-c16 leading-none font-bold no-underline transition-colors duration-200 first:-ml-3 max-over12:px-2.25 max-over12:text-c15',
                on ? 'text-t1' : 'text-t4 hover:text-t2 focus-visible:text-t2'
              )}
            >
              {tab.label}
            </Link>
          )
        })}
      </nav>
      <span className="min-w-3 flex-1" />
      <div
        className="flex flex-none items-center gap-2.5 max-over12:gap-1.75"
        aria-label="내 재화와 계정"
      >
        {/* 오른쪽 영역 맨 왼쪽 — 세이프 토글 */}
        <SafeToggle />
        {/* 안쪽 + 원은 위·아래·오른쪽 여백을 같게(6px) — 오른쪽만 12px 이면 원이 알약 안에서 한쪽으로 쏠려 보였다 */}
        <Button
          type="button"
          aria-label="젬 충전"
          onClick={openCharge}
          className={cn(PILL, 'gap-1 pr-1.5 pb-0.125 pl-3 max-over12:pl-2.5')}
          variant="bare"
        >
          <GemIcon className={PILL_ICON} />
          <GemCount className="font-extrabold text-gem" />
          <span
            aria-hidden="true"
            className="ml-1 grid size-5 place-items-center rounded-full bg-foreground/10 text-t2"
          >
            <LuPlus className="size-3" strokeWidth={3} />
          </span>
        </Button>
        {/* [2차 개발] 해금권 알약 — 2026-10-08 2차 범위로 넘김. 되살릴 땐 이 블록 · 위 import(TicketIcon · PILL_PAD · useAppData · openTicketsInfo) · tickets 주석을 푼다
        <button type="button" aria-label="해금권" onClick={openTicketsInfo} className={cn(PILL, PILL_PAD, 'gap-1 pb-0.125')}>
          <TicketIcon className={PILL_ICON} />
          <span>{tickets}</span>
        </button> */}
        {/* 알림 · 마이 — 모바일 머리줄과 같은 부품 */}
        <AccountIcons className="-mr-1" />
      </div>
    </nav>
  )
}
